export type PartySizeDay =
    "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";

export type PartySizeLimits = { min: number; max: number };

export type DayPartySize = { dayOfWeek: PartySizeDay } & {
    minPartySize: number;
    maxPartySize: number;
};

/** Used for any store (or single day) that has no saved row. */
export const DEFAULT_PARTY_SIZE: PartySizeLimits = { min: 2, max: 12 };

export const PARTY_SIZE_ABSOLUTE_MAX = 50;

export const PARTY_SIZE_DAYS: PartySizeDay[] = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

/**
 * The guest-bookable party size (adults + kids + highchairs, see `totalGuests`) for a weekday.
 * Pure so the booking form, AI view and the server all apply the same rule.
 */
export const resolvePartySizeLimits = (
    dayPartySizes: ReadonlyArray<DayPartySize> | null | undefined,
    dayOfWeek: string,
): PartySizeLimits => {
    const row = dayPartySizes?.find((d) => d.dayOfWeek === dayOfWeek);
    return row ? { min: row.minPartySize, max: row.maxPartySize } : DEFAULT_PARTY_SIZE;
};

/** One entry per weekday, filling gaps with the default — what the settings screen edits. */
export const fillWeekPartySizes = (
    dayPartySizes: ReadonlyArray<DayPartySize> | null | undefined,
): DayPartySize[] =>
    PARTY_SIZE_DAYS.map((dayOfWeek) => {
        const { min, max } = resolvePartySizeLimits(dayPartySizes, dayOfWeek);
        return { dayOfWeek, minPartySize: min, maxPartySize: max };
    });

export const isValidPartySizeRange = (min: number, max: number): boolean =>
    Number.isInteger(min) &&
    Number.isInteger(max) &&
    min >= 1 &&
    min <= max &&
    max <= PARTY_SIZE_ABSOLUTE_MAX;

export const formatPartySizeMessage = (
    { min, max }: PartySizeLimits,
    dayOfWeek: string,
    total: number,
): string => `We take ${min} to ${max} guests on ${dayOfWeek}s. You have ${total}.`;
